import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';
import { showAlert } from './alerts.js';
import { t } from 'i18next';

export const sitekey = 'FCMTH06VQP7HMHKH';
// Store captcha instances in a map for dynamic access
const captchaInstances = new Map();

/**
 * Initialize a captcha widget with configurable button IDs and callbacks
 * @param {Object} options - Configuration options
 * @param {string} options.containerId - ID of the container element
 * @param {string|string[]} [options.submitButtonId] - ID(s) of the submit button (optional)
 * @param {Function} [options.onSuccess] - Custom callback for success
 * @param {Function} [options.onError] - Custom callback for error
 * @param {Function} [options.onExpire] - Custom callback for expiration
 * @returns {Object|null} - Captcha widget instance or null if initialization failed
 */
export async function initializeCaptcha(options) {
    // Support both object and string parameter for backward compatibility
    const containerId = typeof options === 'string' ? options : options.containerId;
    
    if (!containerId) {
        console.error('No containerId provided for captcha initialization');
        return null;
    }
    
    // Return existing instance if already initialized
    if (captchaInstances.has(containerId)) {
        console.log(`Captcha ${containerId} already initialized`);
        return captchaInstances.get(containerId);
    }

    try {
        return initCaptchaWidget(options);
    } catch (error) {
        console.error('Failed to load captcha:', error);
        showAlert(t('errors.captchaLoad'), 'error');
        return null;
    }
}

/**
 * Internal function to initialize the captcha widget
 * @param {Object|string} options - Configuration options or containerId string
 * @returns {Object|null} - Captcha widget instance or null
 */
function initCaptchaWidget(options) {
    // Handle backward compatibility
    const containerId = typeof options === 'string' ? options : options.containerId;
    const submitButtonId = typeof options === 'object' ? options.submitButtonId : null;
    const onSuccess = typeof options === 'object' ? options.onSuccess : null;
    const onError = typeof options === 'object' ? options.onError : null;
    const onExpire = typeof options === 'object' ? options.onExpire : null;
    
    // Clear any existing content
    const container = document.getElementById(containerId);
    if (!container) {
        console.error(`Captcha container ${containerId} not found`);
        return null;
    }
    
    // Clear the container
    container.innerHTML = '';
    
    // Add the required attributes directly to the container
    container.className = 'frc-captcha ' + container.className;
    container.dataset.sitekey = sitekey;

    console.log(`Initializing captcha ${containerId}`);

    // Helper function to handle button state changes
    const updateButtonStates = (buttonId, enabled) => {
        if (!buttonId) return;
        
        // Handle multiple button IDs (array) or single button ID (string)
        if (Array.isArray(buttonId)) {
            // It's an array of button IDs
            buttonId.forEach(id => {
                const button = document.getElementById(id);
                if (button) {
                    button.disabled = !enabled;
                }
            });
        } else if (typeof buttonId === 'string') {
            // It's a single button ID
            const button = document.getElementById(buttonId);
            if (button) {
                button.disabled = !enabled;
            }
        }
    };

    try {
        // Create a new SDK instance
        const friendlyCaptcha = new FriendlyCaptchaSDK();
        
        // Initialize Friendly Captcha widget
        const widgetInstance = friendlyCaptcha.createWidget({
            element: container,
            sitekey,
            startMode: "auto"
        });
        container.addEventListener('frc:widget.complete', (solution) => {
            console.log("Captcha solution:", solution.detail.response);
            // Enable submit button(s)
            updateButtonStates(submitButtonId, true);
            
            // Call custom success callback if provided
            if (typeof onSuccess === 'function') {
                onSuccess(solution);
            }
        });
        container.addEventListener('frc:widget.error', (error) => {
            console.error("Captcha error:", error);
                
            // Disable submit button(s)
            updateButtonStates(submitButtonId, false);
            
            // Call custom error callback if provided
            if (typeof onError === 'function') {
                onError(error);
            }
        });
        container.addEventListener('frc:widget.expired', () => {
            // Disable submit button(s) when captcha expires
            updateButtonStates(submitButtonId, false);
                
            // Call custom expire callback if provided
            if (typeof onExpire === 'function') {
                onExpire();
            }
        });
        // Store instance
        captchaInstances.set(containerId, widgetInstance);
        return widgetInstance;
    } catch (error) {
        console.error('Failed to initialize captcha:', error);
        showAlert(t('errors.captchaLoad'), 'error');
        return null;
    }
}

/**
 * Get captcha response
 * @param {string} containerId - ID of the captcha container
 * @returns {string|null} - Captcha solution or null
 */
export function getCaptchaResponse(containerId) {
    if (!containerId) {
        console.error('No containerId provided to get captcha response');
        return null;
    }
    
    const widgetInstance = captchaInstances.get(containerId);
    if (!widgetInstance) {
        console.error(`No widget instance found for ${containerId}`);
        return null;
    }

    // Get the solution from the widget
    try {
        const solution = widgetInstance.getResponse();
        if (!solution) {
            console.error('No captcha solution found');
            showAlert(t('errors.captchaIncomplete'), 'warning');
            return null;
        }
        return solution;
    } catch (error) {
        console.error('Error getting captcha solution:', error);
        return null;
    }
}

/**
 * Reset captcha
 * @param {string|null} containerId - ID of the captcha to reset, or null to reset all
 */
export function resetCaptcha(containerId = null) {
    try {
        if (containerId) {
            // Reset specific captcha
            if (captchaInstances.has(containerId)) {
                captchaInstances.get(containerId).reset();
            }
        } else {
            // Reset all captchas
            captchaInstances.forEach(instance => {
                if (instance) instance.reset();
            });
        }
    } catch (e) {
        console.error('Error resetting captcha:', e);
        showAlert(t('errors.captchaReset'), "error");
    }
}

/**
 * Cleanup captcha when not needed anymore
 * @param {string} containerId - ID of the captcha container to clean up
 */
export function cleanupCaptcha(containerId) {
    if (!containerId) {
        console.error('No containerId provided for captcha cleanup');
        return;
    }
    
    if (captchaInstances.has(containerId)) {
        try {
            captchaInstances.get(containerId).destroy();
            captchaInstances.delete(containerId);
        } catch (err) {
            console.error('Error destroying captcha:', err);
        }
    }
}

/**
 * Check if a captcha is initialized
 * @param {string} containerId - ID of the captcha container
 * @returns {boolean} - Whether the captcha is initialized
 */
export function isCaptchaInitialized(containerId) {
    return captchaInstances.has(containerId) && captchaInstances.get(containerId) !== null;
}

/**
 * Get all initialized captcha instances
 * @returns {Map} - Map of all captcha instances
 */
export function getAllCaptchaInstances() {
    return captchaInstances;
}